import { Injectable, computed, inject, signal } from '@angular/core';
import { HttpClient, HttpContext } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { palette, updatePrimaryPalette } from '@primeuix/themes';
import { environment } from '../../environments/environment';
import { IResponse } from '../core.interface';
import { NO_INTERCEPT } from './auth.interceptor';
import {
  coerceBrandingString,
  normalizeAssetUrl,
  resolveBundledAsset,
  resolveBundledAssetFromPath,
  resolveAssetUrl,
} from './branding-assets';
import type { MeterHelpTypeId } from './branding-assets';

export interface IBrandingTheme {
  primaryColor: string | null;
  secondaryColor: string | null;
  accentColor: string | null;
}

export interface BrandLegalDto {
  termsOfUseUrl: string | null;
  privacyPolicyUrl: string | null;
  aboutUsUrl: string | null;
}

export interface MeterReadingHelpAssetsDto {
  modern: string | null;
  clock: string | null;
  digital: string | null;
}

export interface BrandAssetsDto {
  logo: string | null;
  logoCompact: string | null;
  favicon: string | null;
  loginHero: string | null;
  registrationExamples: string[];
  meterReadingHelp: MeterReadingHelpAssetsDto;
}

export interface PublicBrandingDto {
  brandUuid: string;
  brandCode: string;
  displayName: string;
  domain: string;
  isGgmGroup: boolean;
  sendRequests: boolean;
  legal: BrandLegalDto;
  theme: IBrandingTheme;
  assets: BrandAssetsDto;
}

export interface IBrandingData {
  brandUuid: string | null;
  brandCode: string;
  displayName: string;
  domain: string;
  isGgmGroup: boolean;
  sendRequests: boolean;
  legal: BrandLegalDto;
  theme: IBrandingTheme;
  assets: BrandAssetsDto;
}

type BrandingCache = Record<string, IBrandingData>;

export type BrandingLoadState = 'loading' | 'ready' | 'unavailable';

const GGM_TERMS_URL = 'https://www.gomezgroupmetering.com/pdf/footer/avisoLegal.pdf';
const GGM_PRIVACY_URL = 'https://www.gomezgroupmetering.com/pdf/footer/politica_privacidad.pdf';
const GGM_ABOUT_URL = 'https://www.gomezgroupmetering.com/grupo/grupogomez';

const NEUTRAL_FAVICON = 'assets/img/favicon-neutral.svg';
const UNAVAILABLE_DOCUMENT_TITLE = 'Ha ocurrido un error';

const REGISTRATION_STEMS = ['explicacionAgua1', 'explicacionAgua2', 'explicacionCalef'] as const;

const METER_HELP_STEMS: Record<MeterHelpTypeId, string> = {
  modern: 'send-reading-help-meter',
  clock: 'send-reading-help-clock',
  digital: 'send-reading-help-display',
};

@Injectable({ providedIn: 'root' })
export class BrandingService {
  private http = inject(HttpClient);
  private readonly STORAGE_KEY = 'ggm-branding-v2';
  private objectUrls = new Set<string>();

  private readonly emptyBranding: IBrandingData = {
    brandCode: '',
    displayName: '',
    domain: '',
    isGgmGroup: false,
    brandUuid: null,
    sendRequests: false,
    legal: {
      termsOfUseUrl: null,
      privacyPolicyUrl: null,
      aboutUsUrl: null,
    },
    theme: {
      primaryColor: null,
      secondaryColor: null,
      accentColor: null,
    },
    assets: {
      logo: null,
      logoCompact: null,
      favicon: null,
      loginHero: null,
      registrationExamples: [],
      meterReadingHelp: {
        modern: null,
        clock: null,
        digital: null,
      },
    },
  };

  public readonly branding = signal<IBrandingData>(this.emptyBranding);
  public readonly brandingLoadState = signal<BrandingLoadState>('loading');
  public readonly isBrandingReady = computed(() => this.brandingLoadState() === 'ready');
  public readonly isBrandingUnavailable = computed(
    () => this.brandingLoadState() === 'unavailable',
  );

  public readonly isGGM = computed(() => this.branding().isGgmGroup === true);

  public readonly sendRequestsEnabled = computed(() => this.branding().sendRequests === true);

  public readonly brandUuid = computed(() => {
    return coerceBrandingString(this.branding().brandUuid);
  });

  public readonly termsOfUseUrl = computed(() =>
    this.nonEmptyUrl(this.branding().legal.termsOfUseUrl),
  );
  public readonly privacyPolicyUrl = computed(() =>
    this.nonEmptyUrl(this.branding().legal.privacyPolicyUrl),
  );
  public readonly aboutUsUrl = computed(() => this.nonEmptyUrl(this.branding().legal.aboutUsUrl));

  public readonly logoUrl = computed(() => this.branding().assets.logo);

  public readonly logoMenu = computed(
    () => this.branding().assets.logoCompact ?? this.branding().assets.logo,
  );

  public readonly loginImageUrl = computed(() => this.branding().assets.loginHero);
  public readonly displayName = computed(() => this.branding().displayName);

  public readonly registrationImages = computed(() =>
    this.branding().assets.registrationExamples.filter((item) => item.length > 0),
  );

  public readonly meterReadingHelpImages = computed(
    (): Record<MeterHelpTypeId, string | null> => this.branding().assets.meterReadingHelp,
  );

  async loadBrandingConfig(): Promise<void> {
    this.brandingLoadState.set('loading');
    const domain = this.resolveBrandingDomain();
    const cacheKey = domain.toLowerCase();

    const cached = this.getCachedBranding(cacheKey);
    if (cached) {
      try {
        await this.applyBrandingFromSource(cached);
        return;
      } catch {
        this.clearBrandingCacheEntry(cacheKey);
      }
    }

    try {
      const response = await firstValueFrom(
        this.http.get<IResponse<PublicBrandingDto>>(
          `${environment.apiUrl}/api/public/branding?domain=${encodeURIComponent(domain)}`,
          {
            context: new HttpContext().set(NO_INTERCEPT, true),
            headers: {
              'X-App-Origin': environment.appID,
              'X-GGM-Origin-Channel': 'WEB',
            },
          },
        ),
      );

      const dto = response?.data;
      const brandUuid = coerceBrandingString(dto?.brandUuid);
      if (dto && brandUuid && coerceBrandingString(dto.theme?.primaryColor)) {
        const branding = this.mapPublicBrandingDto(dto);
        this.saveBranding(cacheKey, branding);
        await this.applyBrandingFromSource(branding);
        return;
      }

      this.applyUnavailable();
    } catch {
      this.applyUnavailable();
    }
  }

  public getRequestDomain(): string {
    return this.resolveBrandingDomain();
  }

  public resolveBrandedAsset(assetPath: string): string {
    return resolveBundledAssetFromPath(assetPath, this.isGGM());
  }

  public mapPublicBrandingDto(dto: PublicBrandingDto): IBrandingData {
    const registrationExamples = (dto.assets?.registrationExamples ?? [])
      .map((item) => coerceBrandingString(item))
      .filter((item): item is string => item !== null);

    return {
      brandUuid: coerceBrandingString(dto.brandUuid),
      brandCode: coerceBrandingString(dto.brandCode) ?? '',
      displayName: coerceBrandingString(dto.displayName) ?? '',
      domain: coerceBrandingString(dto.domain) ?? '',
      isGgmGroup: dto.isGgmGroup === true,
      sendRequests: dto.sendRequests === true,
      legal: {
        termsOfUseUrl: coerceBrandingString(dto.legal?.termsOfUseUrl),
        privacyPolicyUrl: coerceBrandingString(dto.legal?.privacyPolicyUrl),
        aboutUsUrl: coerceBrandingString(dto.legal?.aboutUsUrl),
      },
      theme: {
        primaryColor: coerceBrandingString(dto.theme?.primaryColor),
        secondaryColor: coerceBrandingString(dto.theme?.secondaryColor),
        accentColor: coerceBrandingString(dto.theme?.accentColor),
      },
      assets: {
        logo: coerceBrandingString(dto.assets?.logo),
        logoCompact: coerceBrandingString(dto.assets?.logoCompact),
        favicon: coerceBrandingString(dto.assets?.favicon),
        loginHero: coerceBrandingString(dto.assets?.loginHero),
        registrationExamples,
        meterReadingHelp: {
          modern: coerceBrandingString(dto.assets?.meterReadingHelp?.modern),
          clock: coerceBrandingString(dto.assets?.meterReadingHelp?.clock),
          digital: coerceBrandingString(dto.assets?.meterReadingHelp?.digital),
        },
      },
    };
  }

  private resolveBrandingDomain(): string {
    const override = environment.brandingDomainOverride?.trim();
    if (override) {
      return override.toLowerCase();
    }
    if (typeof window === 'undefined') {
      return '';
    }
    return window.location.hostname.toLowerCase();
  }

  private getCachedBranding(domain: string): IBrandingData | null {
    try {
      const raw = localStorage.getItem(this.STORAGE_KEY);
      if (!raw) return null;

      const cache = JSON.parse(raw) as BrandingCache;
      const cached = cache[domain];
      if (!cached?.theme?.primaryColor) return null;

      if (typeof cached.isGgmGroup !== 'boolean') {
        return null;
      }

      if (!coerceBrandingString(cached.brandUuid)) {
        return null;
      }

      if (!cached.assets?.meterReadingHelp) {
        return null;
      }

      const cachedDomain = (cached.domain ?? '').toLowerCase();
      if (cachedDomain && cachedDomain !== domain) {
        return null;
      }

      return this.sanitizeBrandingData(cached);
    } catch {
      return null;
    }
  }

  private sanitizeBrandingData(data: IBrandingData): IBrandingData {
    const registrationExamples = (data.assets.registrationExamples ?? [])
      .map((item) => coerceBrandingString(item))
      .filter((item): item is string => item !== null);

    return {
      ...data,
      brandUuid: coerceBrandingString(data.brandUuid),
      brandCode: coerceBrandingString(data.brandCode) ?? data.brandCode,
      displayName: coerceBrandingString(data.displayName) ?? data.displayName,
      domain: coerceBrandingString(data.domain) ?? data.domain,
      legal: {
        termsOfUseUrl: coerceBrandingString(data.legal.termsOfUseUrl),
        privacyPolicyUrl: coerceBrandingString(data.legal.privacyPolicyUrl),
        aboutUsUrl: coerceBrandingString(data.legal.aboutUsUrl),
      },
      theme: {
        primaryColor: coerceBrandingString(data.theme.primaryColor),
        secondaryColor: coerceBrandingString(data.theme.secondaryColor),
        accentColor: coerceBrandingString(data.theme.accentColor),
      },
      assets: {
        logo: coerceBrandingString(data.assets.logo),
        logoCompact: coerceBrandingString(data.assets.logoCompact),
        favicon: coerceBrandingString(data.assets.favicon),
        loginHero: coerceBrandingString(data.assets.loginHero),
        registrationExamples,
        meterReadingHelp: {
          modern: coerceBrandingString(data.assets.meterReadingHelp?.modern),
          clock: coerceBrandingString(data.assets.meterReadingHelp?.clock),
          digital: coerceBrandingString(data.assets.meterReadingHelp?.digital),
        },
      },
    };
  }

  private saveBranding(domain: string, branding: IBrandingData): void {
    try {
      const raw = localStorage.getItem(this.STORAGE_KEY);
      const cache: BrandingCache = raw ? (JSON.parse(raw) as BrandingCache) : {};
      cache[domain] = branding;
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(cache));
    } catch {
      // Storage no disponible o lleno: se ignora para no romper el arranque.
    }
  }

  private clearBrandingCacheEntry(domain: string): void {
    try {
      const raw = localStorage.getItem(this.STORAGE_KEY);
      if (!raw) {
        return;
      }
      const cache = JSON.parse(raw) as BrandingCache;
      delete cache[domain];
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(cache));
    } catch {
      localStorage.removeItem(this.STORAGE_KEY);
    }
  }

  private async applyBrandingFromSource(branding: IBrandingData): Promise<void> {
    if (!coerceBrandingString(branding.brandUuid)) {
      this.applyUnavailable();
      return;
    }
    await this.applyBranding(branding);
    this.brandingLoadState.set('ready');
  }

  private applyUnavailable(): void {
    this.revokeObjectUrls();
    this.branding.set(this.emptyBranding);
    this.brandingLoadState.set('unavailable');
    this.applyFavicon(NEUTRAL_FAVICON);
    this.applyDocumentTitle(UNAVAILABLE_DOCUMENT_TITLE);
  }

  private async applyBranding(branding: IBrandingData): Promise<void> {
    const isGgm = branding.isGgmGroup === true;
    const merged: IBrandingData = {
      ...branding,
      isGgmGroup: isGgm,
      brandUuid: branding.brandUuid ?? null,
      sendRequests: branding.sendRequests === true,
      legal: {
        termsOfUseUrl: this.pickUrl(branding.legal.termsOfUseUrl, isGgm ? GGM_TERMS_URL : null),
        privacyPolicyUrl: this.pickUrl(
          branding.legal.privacyPolicyUrl,
          isGgm ? GGM_PRIVACY_URL : null,
        ),
        aboutUsUrl: this.pickUrl(branding.legal.aboutUsUrl, isGgm ? GGM_ABOUT_URL : null),
      },
      theme: {
        primaryColor: branding.theme?.primaryColor ?? null,
        secondaryColor: branding.theme?.secondaryColor ?? branding.theme?.primaryColor ?? null,
        accentColor: branding.theme?.accentColor ?? null,
      },
      assets: this.mergeAssetsWithBundledFallbacks(branding.assets, isGgm),
    };

    this.applyColors(merged.theme);

    const hydratedAssets = await this.hydrateRemoteAssets(merged.assets);
    const hydrated: IBrandingData = { ...merged, assets: hydratedAssets };

    this.revokeObjectUrls();
    this.branding.set(hydrated);
    this.trackObjectUrls(hydrated.assets);
    this.applyFavicon(
      hydrated.assets.favicon ??
        (isGgm ? resolveBundledAsset('logo', 'png', true) : hydrated.assets.logo) ??
        NEUTRAL_FAVICON,
    );
  }

  private mergeAssetsWithBundledFallbacks(
    assets: BrandAssetsDto | undefined,
    isGgm: boolean,
  ): BrandAssetsDto {
    const fromApi = assets ?? {
      logo: null,
      logoCompact: null,
      favicon: null,
      loginHero: null,
      registrationExamples: [],
      meterReadingHelp: { modern: null, clock: null, digital: null },
    };

    const logoRaw = coerceBrandingString(fromApi.logo);
    const logoCompactRaw = coerceBrandingString(fromApi.logoCompact);
    const logo = logoRaw
      ? (normalizeAssetUrl(logoRaw) ?? logoRaw)
      : isGgm
        ? resolveBundledAsset('logo', 'png', true)
        : null;
    const logoCompact = logoCompactRaw
      ? (normalizeAssetUrl(logoCompactRaw) ?? logoCompactRaw)
      : logo
        ? logo
        : isGgm
          ? resolveBundledAsset('logo-grupo', 'png', true)
          : null;

    const registrationExamples = REGISTRATION_STEMS.map((stem, index) =>
      resolveAssetUrl(fromApi.registrationExamples[index], stem, 'png', isGgm),
    );

    const meterReadingHelp: MeterReadingHelpAssetsDto = {
      modern: resolveAssetUrl(
        fromApi.meterReadingHelp?.modern,
        METER_HELP_STEMS.modern,
        'png',
        isGgm,
      ),
      clock: resolveAssetUrl(fromApi.meterReadingHelp?.clock, METER_HELP_STEMS.clock, 'png', isGgm),
      digital: resolveAssetUrl(
        fromApi.meterReadingHelp?.digital,
        METER_HELP_STEMS.digital,
        'png',
        isGgm,
      ),
    };

    return {
      logo,
      logoCompact,
      favicon: normalizeAssetUrl(fromApi.favicon),
      loginHero: normalizeAssetUrl(fromApi.loginHero),
      registrationExamples,
      meterReadingHelp,
    };
  }

  private async hydrateRemoteAssets(assets: BrandAssetsDto): Promise<BrandAssetsDto> {
    const registrationExamples = await Promise.all(
      assets.registrationExamples.map((url) => this.toDisplayAssetUrl(url)),
    );

    const meterReadingHelp: MeterReadingHelpAssetsDto = {
      modern: await this.toDisplayAssetUrl(assets.meterReadingHelp.modern),
      clock: await this.toDisplayAssetUrl(assets.meterReadingHelp.clock),
      digital: await this.toDisplayAssetUrl(assets.meterReadingHelp.digital),
    };

    return {
      logo: await this.toDisplayAssetUrl(assets.logo),
      logoCompact: await this.toDisplayAssetUrl(assets.logoCompact),
      favicon: await this.toDisplayAssetUrl(assets.favicon),
      loginHero: await this.toDisplayAssetUrl(assets.loginHero),
      registrationExamples: registrationExamples.map((url) => url ?? ''),
      meterReadingHelp,
    };
  }

  private async toDisplayAssetUrl(url: string | null | undefined): Promise<string | null> {
    if (!url) return null;
    const resolved = normalizeAssetUrl(url) ?? url;
    if (!this.isRemoteAssetUrl(resolved)) return resolved;

    try {
      const blob = await firstValueFrom(
        this.http.get(resolved, {
          responseType: 'blob',
          context: new HttpContext().set(NO_INTERCEPT, true),
          headers: {
            'X-App-Origin': environment.appID,
            'X-GGM-Origin-Channel': 'WEB',
          },
        }),
      );
      return URL.createObjectURL(blob);
    } catch (error) {
      console.error('Error loading branding asset:', resolved, error);
      return resolved;
    }
  }

  private isRemoteAssetUrl(url: string): boolean {
    if (url.startsWith('assets/')) {
      return false;
    }
    const apiBase = environment.apiUrl.replace(/\/$/, '');
    if (url.startsWith(apiBase)) {
      return true;
    }
    return url.includes('/api/public/') || url.includes('/api/');
  }

  private trackObjectUrls(assets: BrandAssetsDto): void {
    for (const value of this.collectAssetUrlValues(assets)) {
      if (value.startsWith('blob:')) {
        this.objectUrls.add(value);
      }
    }
  }

  private collectAssetUrlValues(assets: BrandAssetsDto): string[] {
    return [
      assets.logo,
      assets.logoCompact,
      assets.favicon,
      assets.loginHero,
      ...assets.registrationExamples,
      assets.meterReadingHelp.modern,
      assets.meterReadingHelp.clock,
      assets.meterReadingHelp.digital,
    ].filter((value): value is string => typeof value === 'string' && value.length > 0);
  }

  private revokeObjectUrls(): void {
    for (const url of this.objectUrls) {
      URL.revokeObjectURL(url);
    }
    this.objectUrls.clear();
  }

  private applyFavicon(faviconUrl: string | null): void {
    if (typeof document === 'undefined') return;

    let link =
      document.querySelector<HTMLLinkElement>("link[rel='icon']") ??
      document.querySelector<HTMLLinkElement>("link[rel*='icon']");

    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }

    if (faviconUrl) {
      link.href = faviconUrl;
      if (faviconUrl.endsWith('.svg')) {
        link.type = 'image/svg+xml';
      } else {
        link.removeAttribute('type');
      }
    }
  }

  private applyDocumentTitle(title: string): void {
    if (typeof document === 'undefined') return;
    document.title = title;
  }

  private toCssColor(value: string): string {
    return value.startsWith('#') ? value : `#${value}`;
  }

  private nonEmptyUrl(value: string | null | undefined): string | null {
    return coerceBrandingString(value);
  }

  private pickUrl(value: string | null | undefined, fallback: string | null): string | null {
    const fromApi = this.nonEmptyUrl(value);
    if (fromApi) return fromApi;
    return fallback;
  }

  private applyColors(theme: IBrandingTheme): void {
    const root = document.documentElement;
    const primary = theme.primaryColor ? this.toCssColor(theme.primaryColor) : null;
    const secondary = theme.secondaryColor ? this.toCssColor(theme.secondaryColor) : null;
    const sidebar = theme.accentColor ? this.toCssColor(theme.accentColor) : '#FFFFFF';

    if (primary) {
      const primaryPalette = palette(primary) as Record<string, string>;
      updatePrimaryPalette(primaryPalette);

      const primaryHover = primaryPalette['600'] ?? primary;
      const primaryActive = primaryPalette['700'] ?? primaryHover;
      const primarySoft = primaryPalette['100'] ?? primary;

      root.style.setProperty('--p-primary-color', primary);
      root.style.setProperty('--p-primary-hover-color', primaryHover);
      root.style.setProperty('--p-primary-active-color', primaryActive);
      root.style.setProperty('--p-primary-contrast-color', '#ffffff');
      root.style.setProperty('--p-floatlabel-focus-color', primary);
      root.style.setProperty('--p-button-primary-hover-background', primaryHover);
      root.style.setProperty('--p-highlight-background', primarySoft);
      root.style.setProperty('--p-highlight-color', primary);
      root.style.setProperty('--p-checkbox-checked-background', primary);
      root.style.setProperty('--p-checkbox-checked-border-color', primary);
      root.style.setProperty('--p-checkbox-checked-hover-background', primaryHover);
      root.style.setProperty('--p-checkbox-checked-hover-border-color', primaryHover);
      root.style.setProperty('--p-datatable-row-selected-background', primarySoft);
      root.style.setProperty('--p-datatable-row-selected-color', primary);
      root.style.setProperty('--p-datatable-header-cell-selected-background', primarySoft);
      root.style.setProperty('--p-datatable-header-cell-selected-color', primary);
      root.style.setProperty('--p-datatable-row-toggle-button-selected-hover-color', primary);
    }

    if (secondary) {
      root.style.setProperty('--p-secondary-color', secondary);
      root.style.setProperty('--p-button-outlined-secondary-color', secondary);
      root.style.setProperty('--p-button-outlined-secondary-border-color', secondary);
      root.style.setProperty('--p-button-outlined-secondary-hover-background', secondary);
    }

    root.style.setProperty('--p-sidebar-color', sidebar);
  }
}
